import { Injectable } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class EventService {
  public eventHandler: Subject<any> = new Subject<any>();

  broadcast(eventName: string, data?: any): void {
    this.eventHandler.next({ eventName, data });
  }

  on(eventName: string, callback: any): Subscription {
    return this.eventHandler.pipe(filter(event => event.eventName === eventName)).subscribe(callback);
  }

  // Server Events
  onDownloadCompleteReceived(callback: (uniqueID: string) => void): Subscription {
    return this.on(HRIMServerEvents.downloadComplete, (event: any) => {
      callback(event.data.uniqueID);
    });
  }

  onSendProgressCountReceived(callback: (current: number, total: number, uniqueID: string) => void): Subscription {
    return this.on(HRIMServerEvents.sendProgressCount, (event: any) => {
      callback(event.data.current, event.data.total, event.data.uniqueID);
    });
  }

  onSendProgressFileName(callback: (fileName: string, uniqueID: string) => void): Subscription {
    return this.on(HRIMServerEvents.sendProgressFileName, (event: any) => {
      callback(event.data.fileName, event.data.uniqueID);
    });
  }

  onSendLogoutSignalReceived(callback: () => void): Subscription {
    return this.on(HRIMServerEvents.sendLogoutSignal, () => {
      callback();
    });
  }

  onSendApprovalCountReceived(callback: (approval: string, count: number) => void): Subscription {
    return this.on(HRIMServerEvents.sendApprovalCount, (event: any) => {
      callback(event.data.approval, event.data.count);
    });
  }

  onSendDismissBellNotification(callback: (bellNotificationID: bigint) => void): Subscription {
    return this.on(HRIMServerEvents.sendDismissBellNotification, (event: any) => {
      callback(event.data.bellNotificationID);
    });
  }

  onSendUpdateBellNotification(
    callback: (
      bellNotificationID: bigint,
      title: string,
      description: string,
      dateStarted: Date,
      dateCompleted: Date,
      status: number,
      actionURL: string,
      progress: number
    ) => void
  ): Subscription {
    return this.on(HRIMServerEvents.sendUpdateBellNotification, (event: any) => {
      callback(
        event.data.bellNotificationID,
        event.data.title,
        event.data.description,
        event.data.dateStarted,
        event.data.dateCompleted,
        event.data.status,
        event.data.actionURL == '' ? null : event.data.actionURL,
        event.data.progress
      );
    });
  }

  onSendUpdateBellNotificationProgress(callback: (bellNotificationID: bigint, progress: number) => void): Subscription {
    return this.on(HRIMServerEvents.sendUpdateBellNotificationProgress, (event: any) => {
      callback(BigInt(event.data.bellNotificationID), event.data.progress);
    });
  }

  onSendRefreshBellNotifications(callback: () => void): Subscription {
    return this.on(HRIMServerEvents.sendRefreshBellNotifications, () => {
      callback();
    });
  }

  onSendPortalSettingsUpdateReceived(callback: () => void): Subscription {
    return this.on(HRIMServerEvents.sendPortalSettingsUpdate, () => {
      callback();
    });
  }

  // Browser Events
  openSpinner(msg: string, showProgress: boolean, uniqueID: string): void {
    this.broadcast(HRIMBrowserEvents.openSpinner, { msg, showProgress, uniqueID });
  }

  onOpenSpinnerReceived(callback: (msg: string, showProgress: boolean, uniqueID: string) => void): Subscription {
    return this.on(HRIMBrowserEvents.openSpinner, (event: any) => {
      callback(event.data.msg, event.data.showProgress, event.data.uniqueID);
    });
  }

  updateSpinnerMsg(msg: string, uniqueID: string): void {
    this.broadcast(HRIMBrowserEvents.updateSpinnerMsg, { msg, uniqueID });
  }

  onUpdateSpinnerMsg(callback: (msg: string, uniqueID: string) => void): Subscription {
    return this.on(HRIMBrowserEvents.updateSpinnerMsg, (event: any) => {
      callback(event.data.msg, event.data.uniqueID);
    });
  }

  closeSpinner(uniqueID: string): void {
    this.broadcast(HRIMBrowserEvents.closeSpinner, { uniqueID });
  }

  onCloseSpinnerReceived(callback: (uniqueID: string) => void): Subscription {
    return this.on(HRIMBrowserEvents.closeSpinner, (event: any) => {
      callback(event.data.uniqueID);
    });
  }

  updateProgressSpinnerCount(current: number, total: number, uniqueID: string): void {
    this.broadcast(HRIMServerEvents.sendProgressCount, { current, total, uniqueID });
  }
}

export const HRIMServerEvents = {
  downloadComplete: 'Server_DownloadComplete',
  sendProgressFileName: 'Server_SendProgressFileName',
  sendProgressCount: 'Server_SendProgressCount',
  sendLogoutSignal: 'Server_SendLogoutSignal',
  sendApprovalCount: 'Server_SendApprovalCount',
  sendDismissBellNotification: 'Server_SendDismissBellNotification',
  sendUpdateBellNotification: 'Server_SendUpdateBellNotification',
  sendUpdateBellNotificationProgress: 'Server_SendUpdateBellNotificationProgress',
  sendRefreshBellNotifications: 'Server_SendRefreshBellNotifications',
  sendPortalSettingsUpdate: 'Server_SendPortalSettingsUpdate',
} as const;

export const HRIMBrowserEvents = {
  modulesChanged: 'Local_modulesChanged',
  openSpinner: 'Local_openSpinner',
  closeSpinner: 'Local_closeSpinner',
  updateSpinnerMsg: 'Local_updateSpinnerMsg',
} as const;

export const GlobalSpinnerMessages = {
  loading: 'Loading...',
  pleaseWait: 'Please wait...',
  processing: 'Please wait while the page is processing...',
  retrieved: 'Please wait while your document is retrieved...',
} as const;
